import React, { useEffect, useMemo, useRef, useState } from "react";
import { Overlay, Popover } from "react-bootstrap";
import { Icon } from "@iconify/react";

/** YYYY-MM-DD for a Date in Asia/Kolkata */
export function formatIstYmd(date = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

/** Parse YYYY-MM-DD as a local calendar date (no TZ shift). */
function parseYmd(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function addDaysYmd(ymd, days) {
  const d = parseYmd(ymd);
  if (!d) return "";
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Monday of the IST week containing `ymd`. */
function startOfWeekMonday(ymd) {
  const d = parseYmd(ymd);
  if (!d) return "";
  const day = d.getDay(); // 0 Sun .. 6 Sat
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dayNum = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dayNum}`;
}

export function getIstDatePresetRange(preset) {
  const today = formatIstYmd();
  switch (preset) {
    case "today":
      return { from: today, to: today };
    case "yesterday": {
      const y = addDaysYmd(today, -1);
      return { from: y, to: y };
    }
    case "this_week":
      return { from: startOfWeekMonday(today), to: today };
    case "this_month": {
      const [y, m] = today.split("-");
      return { from: `${y}-${m}-01`, to: today };
    }
    case "this_year": {
      const [y] = today.split("-");
      return { from: `${y}-01-01`, to: today };
    }
    case "all_time":
    default:
      return { from: "", to: "" };
  }
}

function detectPreset(from, to) {
  const presets = [
    "today",
    "yesterday",
    "this_week",
    "this_month",
    "this_year",
    "all_time",
  ];
  for (const key of presets) {
    const range = getIstDatePresetRange(key);
    if (range.from === (from || "") && range.to === (to || "")) return key;
  }
  return "custom";
}

function formatDisplayDdMmYyyy(ymd) {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  if (!y || !m || !d) return ymd;
  return `${d}/${m}/${y}`;
}

const PRESETS = [
  { key: "today", label: "Today", icon: "solar:clock-circle-bold-duotone" },
  {
    key: "yesterday",
    label: "Yesterday",
    icon: "solar:rewind-back-circle-bold-duotone",
  },
  {
    key: "this_week",
    label: "This Week",
    icon: "solar:calendar-bold-duotone",
  },
  {
    key: "this_month",
    label: "This Month",
    icon: "solar:calendar-mark-bold-duotone",
  },
  {
    key: "this_year",
    label: "This Year",
    icon: "solar:calendar-minimalistic-bold-duotone",
  },
  { key: "all_time", label: "All Time", icon: "solar:infinity-bold-duotone" },
];

/**
 * Date range filter popover (IST).
 * Calls onApply({ fromDate, toDate, preset }) with YYYY-MM-DD strings
 * (empty for All Time).
 */
function DateRangeFilterPopover({
  fromDate = "",
  toDate = "",
  onApply,
  buttonClass = "",
  buttonTableStyle,
}) {
  const [show, setShow] = useState(false);
  const targetRef = useRef(null);
  const [draftFrom, setDraftFrom] = useState(fromDate || "");
  const [draftTo, setDraftTo] = useState(toDate || "");
  const [activePreset, setActivePreset] = useState(() =>
    detectPreset(fromDate, toDate),
  );

  useEffect(() => {
    if (!show) return;
    setDraftFrom(fromDate || "");
    setDraftTo(toDate || "");
    setActivePreset(detectPreset(fromDate, toDate));
  }, [show, fromDate, toDate]);

  const triggerLabel = useMemo(() => {
    if (!fromDate && !toDate) return "All Time";
    if (fromDate && toDate && fromDate === toDate) {
      return formatDisplayDdMmYyyy(fromDate);
    }
    if (fromDate || toDate) {
      return `${formatDisplayDdMmYyyy(fromDate) || "…"} – ${
        formatDisplayDdMmYyyy(toDate) || "…"
      }`;
    }
    return "Filter by date";
  }, [fromDate, toDate]);

  const applyRange = (nextFrom, nextTo, preset) => {
    if (nextFrom && nextTo && nextFrom > nextTo) return;
    onApply?.({
      fromDate: nextFrom || "",
      toDate: nextTo || "",
      preset: preset || detectPreset(nextFrom, nextTo),
    });
    setShow(false);
  };

  const selectPreset = (key) => {
    const range = getIstDatePresetRange(key);
    setActivePreset(key);
    setDraftFrom(range.from);
    setDraftTo(range.to);
    // Presets apply immediately and refetch
    applyRange(range.from, range.to, key);
  };

  const handleReset = () => {
    selectPreset("all_time");
  };

  const handleApply = () => {
    applyRange(draftFrom || "", draftTo || "");
  };

  const handleFromChange = (value) => {
    setDraftFrom(value);
    setActivePreset("custom");
  };

  const handleToChange = (value) => {
    setDraftTo(value);
    setActivePreset("custom");
  };

  const invalidRange =
    draftFrom && draftTo && draftFrom > draftTo;

  return (
    <>
      <button
        ref={targetRef}
        type="button"
        className={`btn btn-outline-secondary d-inline-flex align-items-center gap-2 ${buttonClass}`}
        style={
          buttonTableStyle === "square" ? { borderRadius: 0 } : undefined
        }
        onClick={() => setShow((v) => !v)}
      >
        <Icon icon="solar:calendar-bold-duotone" width="18" height="18" />
        <span>{triggerLabel}</span>
        <Icon icon="solar:alt-arrow-down-linear" width="16" height="16" />
      </button>

      <Overlay
        target={targetRef.current}
        show={show}
        placement="bottom-end"
        rootClose
        onHide={() => setShow(false)}
      >
        <Popover
          id="stock-date-range-filter"
          style={{ maxWidth: 360, width: 340 }}
        >
          <Popover.Body className="p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span
                className="fw-semibold text-uppercase"
                style={{ fontSize: 12, letterSpacing: 0.4, color: "#4b5563" }}
              >
                Filter by date (IST)
              </span>
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-danger text-decoration-none"
                onClick={handleReset}
              >
                Reset
              </button>
            </div>

            <div
              className="d-grid gap-2 mb-3"
              style={{ gridTemplateColumns: "1fr 1fr" }}
            >
              {PRESETS.map((p) => {
                const selected = activePreset === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    className="btn btn-sm d-flex align-items-center gap-2 justify-content-start"
                    onClick={() => selectPreset(p.key)}
                    style={{
                      background: selected ? "#0f766e" : "#f3f4f6",
                      color: selected ? "#fff" : "#374151",
                      border: "none",
                      borderRadius: 8,
                      padding: "8px 10px",
                      fontWeight: 500,
                    }}
                  >
                    <Icon icon={p.icon} width="16" height="16" />
                    {p.label}
                  </button>
                );
              })}
            </div>

            <hr className="my-3" />

            <div className="d-flex align-items-center gap-2 mb-2">
              <Icon
                icon="solar:slider-horizontal-bold-duotone"
                width="16"
                height="16"
                className="text-muted"
              />
              <span className="fw-semibold" style={{ fontSize: 13 }}>
                Custom Range
              </span>
            </div>

            <div className="mb-2">
              <label className="form-label mb-1" style={{ fontSize: 12 }}>
                From Date
              </label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={draftFrom}
                onChange={(e) => handleFromChange(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label className="form-label mb-1" style={{ fontSize: 12 }}>
                To Date
              </label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={draftTo}
                min={draftFrom || undefined}
                onChange={(e) => handleToChange(e.target.value)}
              />
              {invalidRange ? (
                <div className="text-danger mt-1" style={{ fontSize: 11 }}>
                  To date must be on or after From date
                </div>
              ) : null}
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-light btn-sm"
                onClick={() => setShow(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-sm text-white"
                style={{ background: "#0f766e", borderColor: "#0f766e" }}
                disabled={invalidRange}
                onClick={handleApply}
              >
                Apply Range
              </button>
            </div>
          </Popover.Body>
        </Popover>
      </Overlay>
    </>
  );
}

export default DateRangeFilterPopover;
